import React from 'react';

import { useFlowState } from '@betterment/js-runtime';
import { Banner, Box, Card, ContentLayoutF, FormikField, H2, H3, Input, ListRow } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { Form } from 'formik';
import * as Yup from 'yup';

import { parseFloatFromNumberInputMask } from '../../../../utils/input-helpers';
import FlowStepLayout from '../../../BettermentForAdvisors/FlowStepLayout';
import { makeUpsertBillingPlanInput } from '../api';
import { MAX_ASSET_BASED_FEE_RATE } from '../constants';
import { BillingPlanFlowValues, PlatformFeePaidBy } from '../data';
import FeeSummaryBox from '../fee-summary-box/FeeSummaryBox';
import { useSubmitHandler } from '../hooks/useSubmitHandler';
import { PlatformFeeBox, platformFeeNettingSchema } from '../platform-fee-box/PlatformFeeBox';
import { getFlowTitle } from '../utils';

const Schema = Yup.object().shape({
  assetBasedFeeRate: Yup.number()
    .transform((_, originalValue: string) => {
      if (originalValue === '') {
        return null;
      }
      const parsedValue = parseFloatFromNumberInputMask(originalValue);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    })
    .required('Advisory fee is required')
    .integer('Must be an integer')
    .min(0, '0 bps minimum')
    .max(MAX_ASSET_BASED_FEE_RATE, `${MAX_ASSET_BASED_FEE_RATE} bps maximum`),
});

export const AssetBasedBillingConfigurationStep: React.FC = () => {
  const {
    flowState: { values: flowValues },
  } = useFlowState<BillingPlanFlowValues>();

  const { submitHandler, errorMessage } = useSubmitHandler();

  const validationSchema = Schema.concat(platformFeeNettingSchema(flowValues.platformFee));

  const platformFeeTiers = flowValues.feeConfiguration?.platformFeeTiers || [];
  return (
    <FlowStepLayout exitToPath={flowValues.exitToPath} title={getFlowTitle(flowValues, 2)}>
      <Box css={{ width: 'container.max', height: 'container.large', justifySelf: 'center' }}>
        <Box css={{ pb: 'space.400' }}>
          <H2>Set asset based billing plan.</H2>
          {errorMessage && (
            <Banner variant="critical" css={{ mt: 'space.400' }}>
              <Banner.Content>{errorMessage}</Banner.Content>
            </Banner>
          )}
        </Box>
        <FormikValidateAfterSubmit
          initialValues={{
            assetBasedFeeRate: flowValues.assetBasedFeeRate,
            platformFeePaidBy: flowValues.platformFeePaidBy,
            sharedPlatformFeeClientPortionBps: flowValues.sharedPlatformFeeClientPortionBps,
          }}
          onSubmit={async (values: {
            assetBasedFeeRate: string;
            platformFeePaidBy: PlatformFeePaidBy;
            sharedPlatformFeeClientPortionBps?: string;
          }) => {
            await submitHandler({
              ...flowValues,
              assetBasedFeeRate: values.assetBasedFeeRate,
              platformFeePaidBy: values.platformFeePaidBy,
              sharedPlatformFeeClientPortionBps: values.sharedPlatformFeeClientPortionBps,
            });
          }}
          validationSchema={validationSchema}
        >
          {({ values }) => (
            <Form>
              <ContentLayoutF>
                <ContentLayoutF.SlotContainer>
                  <ContentLayoutF.Slot1>
                    <Card
                      css={{
                        width: '100%',
                        padding: 'space.300',
                        borderStyle: 'solid',
                        borderWidth: 'thin',
                        borderRadius: 'large',
                        borderColor: 'border',
                        boxShadow: 'none',
                      }}
                    >
                      <FormikField name="assetBasedFeeRate">
                        <ListRow css={{ padding: '0' }}>
                          <ListRow.LeadingContent>
                            <FormikField.Label>
                              <H3 variant="title200">Annual advisory fee</H3>
                            </FormikField.Label>
                          </ListRow.LeadingContent>
                          <ListRow.TrailingContent>
                            <FormikField.Control>
                              <Input mask="number" trailingSymbol="bps" alignment="right" />
                            </FormikField.Control>
                            <FormikField.ErrorMessage />
                          </ListRow.TrailingContent>
                        </ListRow>
                      </FormikField>
                    </Card>
                    <Box css={{ marginTop: 'space.300' }}>
                      <PlatformFeeBox platformFee={flowValues.platformFee} tiers={platformFeeTiers} />
                    </Box>
                  </ContentLayoutF.Slot1>
                  <ContentLayoutF.Slot2>
                    <FeeSummaryBox
                      billingPlan={makeUpsertBillingPlanInput({
                        ...flowValues,
                        assetBasedFeeRate: values.assetBasedFeeRate,
                      })}
                      platformFee={flowValues.platformFee}
                      numberOfClients={flowValues.numberOfClients}
                    />
                  </ContentLayoutF.Slot2>
                </ContentLayoutF.SlotContainer>
              </ContentLayoutF>
            </Form>
          )}
        </FormikValidateAfterSubmit>
      </Box>
    </FlowStepLayout>
  );
};
